/**
 * Source de données du widget Actualités.
 *
 * Phase 1 : données factices (mock) servies via une interface asynchrone et mise
 * en cache, identique au pattern météo (voir `weather.ts`). Pour brancher une
 * vraie source plus tard (backend Copernic, flux RSS, API tierce), il suffit de
 * remplacer le corps de `fetchNewsNetwork` par l'appel réseau : le reste (cache,
 * dédupe, hook `useNews`, composant) est déjà prêt.
 */

/** Catégorie éditoriale d'une actualité (sert au filtrage). */
export type NewsCategory =
  | "avicole"
  | "elevage"
  | "climat"
  | "reglementation"
  | "marche";

export type NewsItem = {
  id: string;
  title: string;
  category: NewsCategory;
  source: string;
  /** Date de publication, ISO 8601. */
  publishedAt: string;
  /** Lien optionnel vers l'article complet. */
  url?: string;
};

export const NEWS_CATEGORY_LABELS: Record<NewsCategory, string> = {
  avicole: "Avicole",
  elevage: "Élevage",
  climat: "Météo & climat",
  reglementation: "Réglementation",
  marche: "Marché",
};

/**
 * Jeu d'actualités factices. Les dates sont des décalages (en heures) appliqués
 * au moment du fetch, pour que l'affichage relatif (« il y a 2 h ») reste réaliste
 * sans dépendre d'un horodatage figé.
 */
const MOCK_NEWS: Array<Omit<NewsItem, "publishedAt"> & { hoursAgo: number }> = [
  {
    id: "n1",
    title: "Grippe aviaire : le niveau de risque relevé à « élevé » sur tout le territoire",
    category: "avicole",
    source: "Ministère de l'Agriculture",
    hoursAgo: 2,
  },
  {
    id: "n2",
    title: "Cours de l'aliment volaille en léger repli pour le troisième mois consécutif",
    category: "marche",
    source: "ITAVI",
    hoursAgo: 5,
  },
  {
    id: "n3",
    title: "Épisode de fortes chaleurs annoncé en fin de semaine : adaptez la ventilation",
    category: "climat",
    source: "Météo-France",
    hoursAgo: 9,
  },
  {
    id: "n4",
    title: "Bien-être animal : nouvelles obligations de densité applicables au 1er janvier",
    category: "reglementation",
    source: "Journal Officiel",
    hoursAgo: 26,
  },
  {
    id: "n5",
    title: "Performances techniques : retour d'expérience sur la conduite en lumière naturelle",
    category: "elevage",
    source: "Réussir Aviculture",
    hoursAgo: 30,
  },
  {
    id: "n6",
    title: "Biosécurité : rappel des bonnes pratiques de désinfection des sas d'entrée",
    category: "avicole",
    source: "GDS France",
    hoursAgo: 48,
  },
];

const NEWS_TTL_MS = 10 * 60 * 1000; // 10 minutes
const CACHE_PREFIX = "news:";

type CachedNews = { items: NewsItem[]; fetchedAt: number };

const cacheKey = (category: NewsCategory | "all") => `${CACHE_PREFIX}${category}`;

function readCache(key: string): CachedNews | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as CachedNews) : null;
  } catch {
    return null;
  }
}

function writeCache(key: string, entry: CachedNews): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Quota plein / mode privé : le cache est best-effort.
  }
}

// Dédupe les appels simultanés pour une même catégorie (plusieurs widgets montés
// en même temps via le lazyLoad de la grille).
const inFlight = new Map<string, Promise<CachedNews>>();

/**
 * Implémentation « réseau » : à remplacer par un vrai fetch le moment venu.
 * Ici, on renvoie les données mock filtrées par catégorie et triées du plus
 * récent au plus ancien, avec des dates calculées par rapport à maintenant.
 */
async function fetchNewsNetwork(
  category: NewsCategory | "all"
): Promise<NewsItem[]> {
  const now = Date.now();
  return MOCK_NEWS.filter(
    (item) => category === "all" || item.category === category
  )
    .map(({ hoursAgo, ...item }) => ({
      ...item,
      publishedAt: new Date(now - hoursAgo * 3600 * 1000).toISOString(),
    }))
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

/**
 * Récupère les actualités avec cache TTL et dédupe des requêtes en vol.
 * Retourne la liste + l'horodatage du fetch réel (pour « Mis à jour à … »).
 */
export async function fetchNews(
  category: NewsCategory | "all" = "all",
  force = false
): Promise<CachedNews> {
  const key = cacheKey(category);

  if (!force) {
    const cached = readCache(key);
    if (cached && Date.now() - cached.fetchedAt < NEWS_TTL_MS) {
      return cached;
    }
    const pending = inFlight.get(key);
    if (pending) return pending;
  }

  const request = fetchNewsNetwork(category)
    .then((items) => {
      const entry: CachedNews = { items, fetchedAt: Date.now() };
      writeCache(key, entry);
      return entry;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
}
